import { useState } from 'react';
import { trpc } from '../../../lib/trpc.js';

type RuleType = 'campaign_roas' | 'account_roas' | 'morning_reenable' | 'geo' | 'custom';
type ActionKind = 'pause' | 'enable' | 'adjust_bid' | 'alert';

interface RuleFormProps {
  tenantId: string;
  onClose: () => void;
  onCreated?: () => void;
}

const RULE_TYPES: { value: RuleType; label: string }[] = [
  { value: 'campaign_roas', label: 'Campaign ROAS' },
  { value: 'account_roas',  label: 'Account ROAS' },
  { value: 'morning_reenable', label: 'Morning Re-Enable' },
  { value: 'geo',    label: 'Geo Automation' },
  { value: 'custom', label: 'Custom' },
];

const METRICS = ['roas', 'cpa', 'spend', 'conversions', 'impressions', 'cvr'];
const OPERATORS = ['<', '>', '<=', '>=', '='];
const ACTIONS: { value: ActionKind; label: string }[] = [
  { value: 'pause',      label: '⏸️ Pause campaign' },
  { value: 'enable',     label: '▶️ Enable campaign' },
  { value: 'adjust_bid', label: '🔄 Adjust bid weight' },
  { value: 'alert',      label: '⚠️ Alert only' },
];

export function RuleForm({ tenantId, onClose, onCreated }: RuleFormProps) {
  const [name, setName] = useState('');
  const [type, setType] = useState<RuleType>('campaign_roas');
  const [metric, setMetric] = useState('roas');
  const [operator, setOperator] = useState('<');
  const [threshold, setThreshold] = useState('1.5');
  const [duration, setDuration] = useState('3');
  const [selectedActions, setSelectedActions] = useState<ActionKind[]>(['pause']);
  const [ruleMode, setRuleMode] = useState<'monitor' | 'active'>('monitor');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggleAction(a: ActionKind) {
    setSelectedActions(prev =>
      prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]
    );
  }

  async function handleSave() {
    if (!name.trim()) { setError('Rule name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      await trpc.guardian.createRule.mutate({
        tenantId,
        name: name.trim(),
        type,
        conditions: { metric, operator, threshold: parseFloat(threshold), durationHours: parseFloat(duration) },
        actions: { kinds: selectedActions },
        mode: ruleMode,
      });
      onCreated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save rule');
      setSaving(false);
    }
  }

  return (
    <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-100">Create Rule</h3>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-300 transition-colors text-xl leading-none">×</button>
      </div>

      {error && (
        <div className="text-xs text-rose-400 bg-rose-900/20 border border-rose-800/40 rounded-lg px-3 py-2">{error}</div>
      )}

      {/* Name */}
      <div>
        <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Rule Name</label>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. Low ROAS Protection"
          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:border-blue-500 transition-colors"
        />
      </div>

      {/* Type */}
      <div>
        <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Rule Type</label>
        <select
          value={type}
          onChange={e => setType(e.target.value as RuleType)}
          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-blue-500 transition-colors"
        >
          {RULE_TYPES.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      {/* Conditions */}
      <div>
        <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Conditions</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <select
            value={metric}
            onChange={e => setMetric(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-blue-500"
          >
            {METRICS.map(m => <option key={m} value={m}>{m.toUpperCase()}</option>)}
          </select>
          <select
            value={operator}
            onChange={e => setOperator(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-blue-500"
          >
            {OPERATORS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <input
            type="number"
            value={threshold}
            onChange={e => setThreshold(e.target.value)}
            placeholder="Threshold"
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:border-blue-500"
          />
          <div className="relative">
            <input
              type="number"
              value={duration}
              onChange={e => setDuration(e.target.value)}
              placeholder="Hours"
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:border-blue-500"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">hrs</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div>
        <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Actions</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {ACTIONS.map(a => (
            <button
              key={a.value}
              onClick={() => toggleAction(a.value)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors text-left ${
                selectedActions.includes(a.value)
                  ? 'border-blue-500 bg-blue-500/10 text-blue-300'
                  : 'border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-300'
              }`}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>

      {/* Mode */}
      <div>
        <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Mode</label>
        <div className="flex gap-2">
          {(['monitor', 'active'] as const).map(m => (
            <button
              key={m}
              onClick={() => setRuleMode(m)}
              className={`flex-1 py-2 rounded-lg border text-sm font-semibold capitalize transition-colors ${
                ruleMode === m
                  ? m === 'active'
                    ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                    : 'border-amber-500 bg-amber-500/10 text-amber-400'
                  : 'border-slate-700 text-slate-500 hover:border-slate-500 hover:text-slate-300'
              }`}
            >
              {m === 'active' ? '⚡ Active' : '👁️ Monitor'}
            </button>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="flex gap-3 pt-2">
        <button
          onClick={onClose}
          disabled={saving}
          className="flex-1 py-2 rounded-lg border border-slate-700 text-sm text-slate-400 hover:text-slate-300 hover:border-slate-500 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-sm font-semibold text-white transition-colors"
        >
          {saving ? 'Saving…' : 'Save Rule'}
        </button>
      </div>
    </div>
  );
}
